import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NotificationsService } from 'angular2-notifications';
import { OfframpService } from '../../services/offramp.service';
import { Ng4LoadingSpinnerService } from 'ng4-loading-spinner';
import * as XLSX from 'xlsx';

const defaults = {
    notif: {
        timeOut: 3000,
        showProgressBar: true,
        pauseOnHover: false,
        clickToClose: true,
        maxLength: 50
    }
}

// Must match the column names read by bulkCreateOfframpTrades in the backend
const CSV_COLUMNS = ['Date', 'Type', 'LiquidityProvider', 'ExchangeQuoteId', 'Rate', 'Qty', 'FeePercent', 'TxHash', 'ExchangeTradeStatus'];
const MAX_ROWS = 1000;

@Component({
    selector: 'app-bulk-upload-offramp-trade',
    templateUrl: './bulk-upload-offramp-trade.component.html',
    styleUrls: ['./bulk-upload-offramp-trade.component.css']
})
export class BulkUploadOfframpTradeDialog implements OnInit {
    liquidityProviderList: any[] = [];
    selectedFile: File = null;
    rows: any[] = [];
    isSubmitting: boolean = false;
    result: any = null;

    constructor(
        public dialogRef: MatDialogRef<BulkUploadOfframpTradeDialog>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private notifSer: NotificationsService,
        public offrampService: OfframpService,
        private spinnerService: Ng4LoadingSpinnerService
    ) { }

    ngOnInit() {
        const filter = { _csrf: this.data._csrf, limit: 10000, page: 1 };
        this.offrampService.getAllOfframpExchanges(filter).subscribe(
            (response: any) => {
                this.liquidityProviderList = (response && response.data) || [];
            },
            () => {
                this.liquidityProviderList = [];
            }
        );
    }

    get liquidityProviderNames(): string {
        return this.liquidityProviderList.map(p => p.name).join(', ');
    }

    get failedResults(): any[] {
        return this.result ? this.result.results.filter(r => r.status === 'FAILED') : [];
    }

    downloadSample() {
        const providerName = this.liquidityProviderList.length ? this.liquidityProviderList[0].name : 'Binance';
        const sampleRows = [
            CSV_COLUMNS,
            ['2026-01-15', 'SELL', providerName, '', '92.50', '1000', '0.118', '0xabc123', 'COMPLETED'],
            ['2026-01-15', 'BUY', providerName, '', '90.10', '250.5', '', '', 'PENDING'],
        ];
        const csv = sampleRows.map(row => row.map(value => this.escapeCsv(value)).join(',')).join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'offramp_trades_sample.csv';
        link.click();
        URL.revokeObjectURL(url);
    }

    private escapeCsv(value: string): string {
        return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
    }

    onFileChange(event: any) {
        this.selectedFile = null;
        this.rows = [];
        this.result = null;

        const file: File = event.target.files && event.target.files[0];
        if (!file) {
            return;
        }
        if (!file.name.toLowerCase().endsWith('.csv')) {
            this.notifSer.error('Error', 'Please select a .csv file', defaults.notif);
            event.target.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            try {
                // raw: true keeps every cell as the literal text from the file, so
                // dates/ids aren't reinterpreted as numbers
                const workbook = XLSX.read(reader.result as string, { type: 'string', raw: true });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const rows: any[] = XLSX.utils.sheet_to_json(sheet, { raw: false, defval: '' });

                const headers = rows.length ? Object.keys(rows[0]) : [];
                const missing = CSV_COLUMNS.filter(c => !headers.includes(c));
                if (missing.length) {
                    this.notifSer.error('Error', `Missing columns: ${missing.join(', ')}`, defaults.notif);
                    return;
                }
                if (rows.length > MAX_ROWS) {
                    this.notifSer.error('Error', `A maximum of ${MAX_ROWS} rows can be uploaded at once`, defaults.notif);
                    return;
                }

                this.selectedFile = file;
                this.rows = rows;
            } catch (e) {
                this.notifSer.error('Error', 'Could not read the CSV file', defaults.notif);
            }
        };
        reader.readAsText(file);
    }

    submit() {
        if (!this.rows.length || this.isSubmitting) {
            return;
        }

        this.isSubmitting = true;
        this.spinnerService.show();
        this.offrampService.bulkCreateOfframpTrades({ rows: this.rows, _csrf: this.data._csrf }).subscribe(
            (response) => {
                this.isSubmitting = false;
                this.spinnerService.hide();
                if (response.status || response.success) {
                    this.result = response.data;
                    const notify = this.result.failed ? 'warn' : 'success';
                    this.notifSer[notify]('Bulk Upload', response.message, defaults.notif);
                } else {
                    this.notifSer.error('Error', response.message || 'Bulk upload failed', defaults.notif);
                }
            },
            (err) => {
                this.isSubmitting = false;
                this.spinnerService.hide();
                this.notifSer.error('Error', (err.error && err.error.message) || 'Bulk upload failed', defaults.notif);
            }
        );
    }

    onClose() {
        this.dialogRef.close({ success: !!(this.result && this.result.created) });
    }
}



// WEBPACK FOOTER //
// ./src/app/offramp-trades/bulk-upload-offramp-trade/bulk-upload-offramp-trade.component.ts